// Volunteer Activity Log — admin view of volunteer check-in/out history
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, getUserRole } from '../firebase/authService';
import { canAccessAdmin, getServiceType } from '../utils/roles';
import { fetchVolunteerCheckInsInRange } from '../firebase/firestoreService';
import '../styles/ActivityLog.css';

// Timeframes offered by the filter, in days back from today.
const TIMEFRAMES = [
  { value: 1, label: 'Today' },
  { value: 7, label: 'Past Week' },
  { value: 30, label: 'Past Month' },
  { value: 90, label: 'Past Quarter' },
  { value: 180, label: 'Past 6 Months' },
  { value: 365, label: 'Past Year' },
];

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value.seconds !== undefined) return new Date(value.seconds * 1000);
  if (value._seconds !== undefined) return new Date(value._seconds * 1000);
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (value) => {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

const serviceLabel = (serviceId) => {
  const info = getServiceType(serviceId);
  if (info) return info.name;
  if (serviceId === 'unscheduled') return 'Off-schedule';
  return serviceId || '—';
};

const VolunteerActivityLog = () => {
  const navigate = useNavigate();
  const user = getCurrentUser();

  const [authorized, setAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [records, setRecords] = useState([]);

  const [timeframe, setTimeframe] = useState(7);
  const [search, setSearch] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [operatorFilter, setOperatorFilter] = useState('all');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    getUserRole(user.uid).then((role) => {
      if (!canAccessAdmin(role)) {
        navigate('/dashboard');
      } else {
        setAuthorized(true);
      }
    });
  }, [user, navigate]);

  const load = useCallback(async (days) => {
    try {
      setLoading(true);
      setError('');

      const end = new Date();
      end.setHours(23, 59, 59, 999);
      const start = new Date();
      start.setDate(start.getDate() - (days - 1));
      start.setHours(0, 0, 0, 0);

      setRecords(await fetchVolunteerCheckInsInRange(start, end));
    } catch (err) {
      console.error('Error loading volunteer activity:', err);
      setError(`Failed to load volunteer activity: ${err.message || 'Please try again.'}`);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authorized) load(timeframe);
  }, [authorized, timeframe, load]);

  // Filter option lists come from the loaded records so they only ever offer
  // values that actually appear in the current range.
  const services = useMemo(() => {
    const set = new Set(records.map(r => r.serviceType).filter(Boolean));
    return Array.from(set).sort();
  }, [records]);

  const operators = useMemo(() => {
    const set = new Set(records.map(r => r.checkInByName).filter(Boolean));
    return Array.from(set).sort();
  }, [records]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return records.filter((r) => {
      if (serviceFilter !== 'all' && r.serviceType !== serviceFilter) return false;
      if (operatorFilter !== 'all' && r.checkInByName !== operatorFilter) return false;
      if (!term) return true;
      const name = `${r.volunteerFirstName || ''} ${r.volunteerLastName || ''}`.toLowerCase();
      return (
        name.includes(term) ||
        (r.checkInByName || '').toLowerCase().includes(term) ||
        (r.notes || '').toLowerCase().includes(term)
      );
    });
  }, [records, search, serviceFilter, operatorFilter]);

  const stats = useMemo(() => {
    const uniqueVolunteers = new Set(filtered.map(r => r.volunteerId)).size;
    const ccbFailures = filtered.filter(
      r => r.ccbAttendanceResult && r.ccbAttendanceResult.success === false
    ).length;
    const notSyncedToCCB = filtered.filter(
      r => r.serviceType !== 'unscheduled' && !r.ccbAttendanceResult
    ).length;
    return { total: filtered.length, uniqueVolunteers, ccbFailures, notSyncedToCCB };
  }, [filtered]);

  const exportCsv = () => {
    const rows = [
      ['Volunteer', 'Service', 'Check-In Time', 'Check-Out Time', 'Status', 'Checked In By', 'Notes', 'CCB Attendance'],
      ...filtered.map((r) => [
        `${r.volunteerFirstName || ''} ${r.volunteerLastName || ''}`.trim(),
        serviceLabel(r.serviceType),
        formatDateTime(r.checkInTime),
        formatDateTime(r.checkOutTime),
        r.status || 'checked_in',
        r.checkInByName || '',
        r.notes || '',
        r.ccbAttendanceResult
          ? (r.ccbAttendanceResult.success ? 'Recorded' : `Failed: ${r.ccbAttendanceResult.error || 'unknown'}`)
          : (r.serviceType === 'unscheduled' ? 'N/A' : 'Not recorded'),
      ]),
    ];
    const csv = rows
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `volunteer-activity-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authorized) {
    return <div className="loading"><div className="spinner"></div><p>Loading...</p></div>;
  }

  return (
    <div className="page-container activity-log-page">
      <div className="admin-header-nav">
        <button className="btn-back" onClick={() => navigate('/admin')}>
          ← Back to Admin Panel
        </button>
        <h1 className="admin-title">Volunteer Activity Log</h1>
      </div>

      {error && <div className="activity-log-banner error">{error}</div>}

      <div className="activity-log-controls">
        <div className="control-group">
          <label htmlFor="val-timeframe">Timeframe</label>
          <select
            id="val-timeframe"
            className="control-select"
            value={timeframe}
            onChange={(e) => setTimeframe(parseInt(e.target.value, 10))}
            disabled={loading}
          >
            {TIMEFRAMES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="val-service">Service</label>
          <select
            id="val-service"
            className="control-select"
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
          >
            <option value="all">All Services</option>
            {services.map(s => (
              <option key={s} value={s}>{serviceLabel(s)}</option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="val-operator">Checked In By</label>
          <select
            id="val-operator"
            className="control-select"
            value={operatorFilter}
            onChange={(e) => setOperatorFilter(e.target.value)}
          >
            <option value="all">Anyone</option>
            {operators.map(o => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>

        <div className="control-group grow">
          <label htmlFor="val-search">Search</label>
          <input
            id="val-search"
            type="text"
            className="control-input"
            placeholder="Volunteer, operator, or note…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="control-group">
          <label>&nbsp;</label>
          <button
            className="btn btn-secondary"
            onClick={exportCsv}
            disabled={loading || filtered.length === 0}
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="activity-log-stats">
        <div className="activity-stat">
          <div className="activity-stat-number">{stats.total}</div>
          <div className="activity-stat-label">Check-Ins</div>
        </div>
        <div className="activity-stat">
          <div className="activity-stat-number">{stats.uniqueVolunteers}</div>
          <div className="activity-stat-label">Unique Volunteers</div>
        </div>
        <div className={`activity-stat ${stats.ccbFailures > 0 ? 'warn' : ''}`}>
          <div className="activity-stat-number">{stats.ccbFailures}</div>
          <div className="activity-stat-label">CCB Sync Failures</div>
        </div>
        <div className={`activity-stat ${stats.notSyncedToCCB > 0 ? 'warn' : ''}`}>
          <div className="activity-stat-number">{stats.notSyncedToCCB}</div>
          <div className="activity-stat-label">Missing CCB Record</div>
        </div>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner"></div><p>Loading volunteer activity...</p></div>
      ) : filtered.length === 0 ? (
        <p className="activity-log-empty">No volunteer activity for this timeframe.</p>
      ) : (
        <div className="activity-log-table">
          <table>
            <thead>
              <tr>
                <th>Volunteer</th>
                <th>Service</th>
                <th>Checked In</th>
                <th>Checked Out</th>
                <th>Status</th>
                <th>Checked In By</th>
                <th>Notes</th>
                <th>CCB</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const ccb = r.ccbAttendanceResult;
                return (
                  <tr key={r.id}>
                    <td className="cell-strong">
                      {r.volunteerFirstName} {r.volunteerLastName}
                    </td>
                    <td>{serviceLabel(r.serviceType)}</td>
                    <td>{formatDateTime(r.checkInTime)}</td>
                    <td>{formatDateTime(r.checkOutTime)}</td>
                    <td>
                      <span className={`activity-badge ${r.status === 'auto_checked_out' ? 'neutral' : 'ok'}`}>
                        {r.status === 'auto_checked_out' ? 'Auto checked out' : 'Checked in'}
                      </span>
                    </td>
                    <td>{r.checkInByName || '—'}</td>
                    <td className="cell-notes" title={r.notes || ''}>{r.notes || '—'}</td>
                    <td>
                      {r.serviceType === 'unscheduled' ? (
                        <span className="activity-badge neutral">N/A</span>
                      ) : ccb ? (
                        <span
                          className={`activity-badge ${ccb.success ? 'ok' : 'bad'}`}
                          title={ccb.success ? `Event ${ccb.eventId}` : (ccb.error || 'Failed')}
                        >
                          {ccb.success ? 'Recorded' : 'Failed'}
                        </span>
                      ) : (
                        <span className="activity-badge warn">Not recorded</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VolunteerActivityLog;
